import {
    IInteractionMessage,
    IInteractionMessagesGetResponse,
    IInteractionMessageWritingIssue,
    Route,
} from "@edgetier/watchtower-types";
import { AxiosError } from "axios";
import { useQuery, UseQueryOptions } from "react-query";
import { axios } from "utilities/axios";
import { getAllPaginatedItems } from "utilities/get-all-paginated-items";

/**
 * Request an interaction's messages from the backend.
 * @param interactionId The ID of the interaction who's messages we're requesting.
 * @returns The interaction messages.
 */
const requestInteractionMessages = async (interactionId: string) => {
    const { data } = await axios.get<IInteractionMessagesGetResponse>(
        Route.InteractionMessages.replace(":interactionId", interactionId)
    );
    return data.items;
};

/**
 * A query for requesting an interaction's messages from the backend.
 * @param interactionId The ID of the interaction who's messages we're requesting.
 */
export const useInteractionMessages = <T = IInteractionMessage[]>(
    interactionId: string,
    options: UseQueryOptions<IInteractionMessage[], AxiosError, T> = {}
) => {
    return useQuery<IInteractionMessage[], AxiosError, T>(
        [Route.InteractionMessages, interactionId],
        () => requestInteractionMessages(interactionId),
        options
    );
};

/**
 * Group writing issues by interaction message ID.
 * @param groupedIssues The writing issues already grouped.
 * @param writingIssue  The current writing issue to group.
 * @returns An object where the keys are interaction message IDs and the values are an array of writing issues.
 */
export const groupWritingIssuesByMessage = (
    groupedIssues: {
        [interactionMessageId: string]: IInteractionMessageWritingIssue[];
    },
    writingIssue: IInteractionMessageWritingIssue
) => {
    const { interactionMessageId } = writingIssue;
    if (interactionMessageId in groupedIssues) {
        return {
            ...groupedIssues,
            [interactionMessageId]: [...groupedIssues[interactionMessageId], writingIssue],
        };
    }
    return { ...groupedIssues, [interactionMessageId]: [writingIssue] };
};

/**
 * Request an interaction's writing issues from the backend.
 * @param interactionId The ID of the interaction who's writing issues we're requesting.
 * @returns The interaction writing issues.
 */
const requestInteractionMessagesWritingIssues = async (interactionId: string) => {
    const data = await getAllPaginatedItems<IInteractionMessageWritingIssue>(
        Route.InteractionMessagesWritingIssues.replace(":interactionId", interactionId)
    );
    return data;
};

/**
 * A query for requesting an interaction's message writing issues.
 * @param interactionId The ID of the interaction who's writing issues we're requesting.
 */
export const useInteractionMessagesWritingIssues = <T = IInteractionMessageWritingIssue[]>(
    interactionId: string,
    options: UseQueryOptions<IInteractionMessageWritingIssue[], AxiosError, T> = {}
) => {
    return useQuery<IInteractionMessageWritingIssue[], AxiosError, T>(
        [Route.InteractionMessagesWritingIssues, interactionId],
        () => requestInteractionMessagesWritingIssues(interactionId),
        options
    );
};
